// src/types/updates.ts
import { Timestamp } from 'firebase/firestore';

/**
 * A news item.
 *
 * WHAT CHANGED AND WHY. The original shape was `{title, content, isNew}` and every field of it
 * was a manual chore. `isNew` never expired, so the badge stayed until somebody remembered to
 * clear it; there was no draft, so saving was publishing; and `createdBy` held a uid that was
 * displayed nowhere, so "who wrote this" had no answer.
 *
 * ONE FIELD CARRIES DRAFT, SCHEDULED AND PUBLISHED. `publishedAt` is null for a draft, a future
 * time for something scheduled, and a past time once it is live. That collapses three states
 * into one comparison — `publishedAt <= now` — which is also a query the public feed can make
 * against a single index, and a condition the security rules can check.
 */

export type UpdateCategory = 'feature' | 'fix' | 'balance' | 'event';

export const UPDATE_CATEGORIES: { id: UpdateCategory; label: string; hint: string }[] = [
    { id: 'feature', label: 'Uus', hint: 'Uus funktsioon või sisu' },
    { id: 'fix', label: 'Parandus', hint: 'Vea parandus' },
    { id: 'balance', label: 'Tasakaal', hint: 'Numbrid, hinnad, tasakaalustus' },
    { id: 'event', label: 'Sündmus', hint: 'Ajutine sündmus või kampaania' },
];

export const DEFAULT_CATEGORY: UpdateCategory = 'feature';

export interface DatabaseUpdate {
    id?: string;
    title: string;
    content: string;
    category: UpdateCategory;

    /** null = draft. Future = scheduled. Past = live. */
    publishedAt: Timestamp | Date | null;

    /** The UUS badge expires by itself at this moment. null = never badged. */
    newUntil: Timestamp | Date | null;

    /** The release this shipped in, e.g. "51.3". Free text; nothing derives from it. */
    releaseVersion: string | null;

    createdAt: Timestamp | Date;
    updatedAt: Timestamp | Date;
    createdBy: string;
    /** Denormalised at write time: reading it back would need the users tab. */
    createdByName: string;
    updatedBy?: string;
    updatedByName?: string;
}

export interface UpdateWriteData {
    title: string;
    content: string;
    category: UpdateCategory;
    publishedAt: Date | null;
    newUntil: Date | null;
    releaseVersion: string | null;
}

/** A Firestore Timestamp, a Date, or nothing — normalised to a Date. */
export const toDate = (value: unknown): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const stamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof stamp.toDate === 'function') return stamp.toDate();
    if (typeof stamp.seconds === 'number') return new Date(stamp.seconds * 1000);
    return null;
};

export type UpdateStatus = 'draft' | 'scheduled' | 'published';

export const statusOf = (update: Pick<DatabaseUpdate, 'publishedAt'>, now = new Date()): UpdateStatus => {
    const at = toDate(update.publishedAt);
    if (!at) return 'draft';
    return at.getTime() > now.getTime() ? 'scheduled' : 'published';
};

/** Whether the UUS badge is still running. Derived, never stored. */
export const isFlaggedNew = (update: Pick<DatabaseUpdate, 'newUntil'>, now = new Date()): boolean => {
    const until = toDate(update.newUntil);
    return until !== null && until.getTime() > now.getTime();
};
